import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Autocomplete,
  TextField,
  CircularProgress,
  Box,
  Typography,
  Chip
} from '@mui/material';
import { Search, Business } from '@mui/icons-material';
import apiClient from '../../services/api';

interface VendorSuggestion {
  vendor_name: string;
  uei: string | null;
  display: string;
  type: string;
}

interface VendorAutocompleteProps {
  onSelect: (vendor: VendorSuggestion | null) => void;
  onInputChange?: (value: string) => void;
  placeholder?: string;
  fullWidth?: boolean;
  size?: 'small' | 'medium';
  autoFocus?: boolean;
}

const VendorAutocomplete: React.FC<VendorAutocompleteProps> = ({
  onSelect,
  onInputChange,
  placeholder = "Search by vendor name or UEI...",
  fullWidth = true,
  size = 'medium',
  autoFocus = false
}) => {
  const [open, setOpen] = useState(false);
  const [options, setOptions] = useState<VendorSuggestion[]>([]);
  const [inputValue, setInputValue] = useState('');
  const [value, setValue] = useState<VendorSuggestion | null>(null);
  const [loading, setLoading] = useState(false);
  const debounceTimer = useRef<NodeJS.Timeout | null>(null);

  // Fetch vendors function
  const fetchVendors = useCallback(async (query: string) => {
    if (query.length < 2) {
      setOptions([]);
      return;
    }

    setLoading(true);
    try {
      const response = await apiClient.get('/vendor/autocomplete', {
        params: {
          q: query,
          limit: 10
        }
      });
      
      if (response.data.suggestions) {
        setOptions(response.data.suggestions);
      }
    } catch (error) {
      console.error('Autocomplete error:', error);
      setOptions([]);
    } finally {
      setLoading(false);
    }
  }, []);

  // Handle input change with debouncing
  useEffect(() => {
    if (inputValue === '') {
      setOptions([]);
      return;
    }

    // Clear previous timer
    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
    }

    // Set new timer
    debounceTimer.current = setTimeout(() => {
      fetchVendors(inputValue);
    }, 300);

    // Cleanup
    return () => {
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current);
      }
    };
  }, [inputValue, fetchVendors]);

  return (
    <Autocomplete
      fullWidth={fullWidth}
      size={size}
      freeSolo
      open={open}
      onOpen={() => setOpen(true)}
      onClose={() => setOpen(false)}
      value={value}
      onChange={(event, newValue) => {
        if (typeof newValue === 'string') {
          // User entered custom text
          setValue(null);
          onSelect(null);
        } else {
          // User selected from dropdown
          setValue(newValue);
          onSelect(newValue);
        }
      }}
      inputValue={inputValue}
      onInputChange={(event, newInputValue) => {
        setInputValue(newInputValue);
        // Pass the input value back to parent
        if (onInputChange) {
          onInputChange(newInputValue);
        }
      }}
      options={options}
      loading={loading}
      getOptionLabel={(option) => {
        if (typeof option === 'string') {
          return option;
        }
        return option.display;
      }}
      isOptionEqualToValue={(option, value) => {
        if (typeof option === 'string' || typeof value === 'string') {
          return false;
        }
        return option.uei === value.uei && option.vendor_name === value.vendor_name;
      }}
      noOptionsText={
        inputValue.length < 2 
          ? "Type at least 2 characters to search" 
          : "Press Enter to search for all matching vendors"
      }
      renderInput={(params) => (
        <TextField
          {...params}
          placeholder={placeholder}
          autoFocus={autoFocus}
          onKeyPress={(e) => {
            if (e.key === 'Enter' && inputValue.trim()) {
              // Close the dropdown when Enter is pressed
              setOpen(false);
            }
          }}
          InputProps={{
            ...params.InputProps,
            startAdornment: (
              <>
                <Search sx={{ color: 'action.active', mr: 1 }} />
                {params.InputProps.startAdornment}
              </>
            ),
            endAdornment: (
              <>
                {loading ? <CircularProgress color="inherit" size={20} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
      renderOption={(props, option) => (
        <Box component="li" {...props}>
          <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
            <Business sx={{ mr: 1.5, color: 'action.active', flexShrink: 0 }} />
            <Box sx={{ flexGrow: 1 }}>
              <Typography variant="body1">
                {option.vendor_name}
              </Typography>
              {option.uei && (
                <Typography variant="caption" color="textSecondary">
                  UEI: {option.uei}
                </Typography>
              )}
            </Box>
          </Box>
        </Box>
      )}
    />
  );
};

export default VendorAutocomplete;